// workspace.jsx — shared, localStorage-backed store for the Research workspace.
// Supports MULTIPLE named source sheets, a shared collected-sources pool, and
// lightweight saved-source folders that are independent of source sheets.
// Shape: { collected:[srcKey...], folders:[{id,name,items:[srcKey...]}], sourceCache:{[srcKey]:source}, sheets:[...], activeId }

const WS_KEY = "mayanos-workspace-v1";

let _wsId = Date.now();
const wsUid = (p) => (p || "b") + (_wsId++).toString(36);

function _emptySheet(title) { return { id: wsUid("s"), title: title || "Untitled source sheet", workingTexts: [], blocks: [] }; }
function _wsInitial() { const s = _emptySheet("Untitled source sheet"); return { collected: [], folders: [], sourceCache: {}, sheets: [s], activeId: s.id }; }

function _normalize(v) {
  if (v && Array.isArray(v.sheets) && v.sheets.length) {
    const sheets = v.sheets.map((sheet) => ({ ...sheet, workingTexts: Array.isArray(sheet.workingTexts) ? sheet.workingTexts : [], blocks: Array.isArray(sheet.blocks) ? sheet.blocks : [] }));
    const folders = Array.isArray(v.folders) ? v.folders.filter(Boolean).map((folder) => ({
      id: String(folder.id || wsUid("f")),
      name: String(folder.name || "Saved sources"),
      items: Array.isArray(folder.items) ? [...new Set(folder.items.map(String))] : [],
    })) : [];
    return { collected: v.collected || [], folders, sourceCache: v.sourceCache || {}, sheets, activeId: v.activeId && sheets.some((s) => s.id === v.activeId) ? v.activeId : sheets[0].id };
  }
  // migrate old single-sheet shape
  if (v && (v.blocks || v.sheetTitle)) {
    const s = { id: wsUid("s"), title: v.sheetTitle || "Untitled source sheet", workingTexts: [], blocks: v.blocks || [] };
    return { collected: v.collected || [], folders: [], sourceCache: v.sourceCache || {}, sheets: [s], activeId: s.id };
  }
  return _wsInitial();
}
function _wsLoad() { try { const raw = localStorage.getItem(WS_KEY); return raw ? _normalize(JSON.parse(raw)) : _wsInitial(); } catch (e) { return _wsInitial(); } }

let _wsState = _wsLoad();
const _wsSubs = new Set();
function _wsSave() { try { localStorage.setItem(WS_KEY, JSON.stringify(_wsState)); } catch (e) {} }
function _wsSet(updater) {
  _wsState = typeof updater === "function" ? updater(_wsState) : updater;
  _wsSave();
  _wsSubs.forEach((fn) => fn(_wsState));
  if (window.MAYANOS_ACCOUNT_SYNC) window.MAYANOS_ACCOUNT_SYNC.schedule();
  try { window.dispatchEvent(new CustomEvent("mayanos-workspace-change", { detail: { workspace: _wsState } })); } catch (e) {}
  if (_wsState.sheets.some((sheet) => sheet && Array.isArray(sheet.blocks) && sheet.blocks.length)) {
    try { window.dispatchEvent(new CustomEvent("mayanos-account-prompt", { detail: { reason: "workspace" } })); } catch (e) {}
  }
}

// mutate the active sheet
function _mutActive(fn) {
  _wsSet((s) => ({ ...s, sheets: s.sheets.map((sh) => sh.id === s.activeId ? fn(sh) : sh) }));
}
const activeSheet = (st) => (st || _wsState).sheets.find((sh) => sh.id === (st || _wsState).activeId) || (st || _wsState).sheets[0];

function currentTextKey() {
  const S = window.SICHA || {};
  return String(S._liveRef || S.id || S.titleEn || "current-text");
}
function sourceKey(srcOrN) {
  if (srcOrN && typeof srcOrN === "object") {
    const s = srcOrN;
    return String(s.srcKey || s.key || [
      currentTextKey(),
      s.id || s.n || "",
      s.srcRef || s.citation || s.title || "",
      s.anchorRef || (Array.isArray(s.anchorRefs) ? s.anchorRefs.join("|") : ""),
    ].join("::"));
  }
  return String(srcOrN || "");
}
function snapshotSource(srcOrN) {
  if (srcOrN && typeof srcOrN === "object") return { ...srcOrN, srcKey: sourceKey(srcOrN) };
  const s = (window.SOURCES || []).find((x) => x.n === srcOrN || String(x.n) === String(srcOrN));
  return s ? { ...s, srcKey: sourceKey(s) } : null;
}
function sourceByToken(token) {
  if (token && typeof token === "object") return snapshotSource(token);
  const key = String(token || "");
  const live = window.SOURCES || [];
  return live.find((s) => sourceKey(s) === key) ||
    (_wsState.sourceCache && _wsState.sourceCache[key]) ||
    live.find((s) => String(s.n) === key || String(s.id) === key) ||
    null;
}
function sourceFromBlock(block) {
  if (!block) return null;
  return sourceByToken(block.srcKey) || block.source || sourceByToken(block.srcN) || null;
}

const WS = {
  get: () => _wsState,
  replace(value) { _wsState = _normalize(value); _wsSave(); _wsSubs.forEach((fn) => fn(_wsState)); },
  active: () => activeSheet(_wsState),
  sourceKey,
  sourceByToken,
  sourceFromBlock,
  sharePayload() {
    const sheet = activeSheet(_wsState);
    const keys = new Set(sheet.blocks.map((block) => block.srcKey).filter(Boolean));
    const sourceCache = Object.fromEntries(Object.entries(_wsState.sourceCache || {}).filter(([key]) => keys.has(key)));
    return { sheet, sourceCache };
  },
  importShared(payload) {
    if (!payload || !payload.sheet || !Array.isArray(payload.sheet.blocks)) return false;
    const sheet = {
      ...payload.sheet,
      id: wsUid("s"),
      title: `${payload.sheet.title || "Shared source sheet"} (shared copy)`,
      blocks: payload.sheet.blocks.map((block) => ({ ...block, id: wsUid() })),
    };
    _wsSet((state) => ({
      ...state,
      sourceCache: { ...(state.sourceCache || {}), ...(payload.sourceCache || {}) },
      sheets: [...state.sheets, sheet],
      activeId: sheet.id,
    }));
    return true;
  },

  // collected pool (shared across sheets)
  collect(srcOrN) {
    const key = sourceKey(srcOrN);
    const source = snapshotSource(srcOrN);
    _wsSet((s) => ({
      ...s,
      sourceCache: source ? { ...(s.sourceCache || {}), [key]: source } : (s.sourceCache || {}),
      collected: s.collected.map(String).includes(key) ? s.collected : [...s.collected, key],
    }));
  },
  addWorkingText(srcOrN) {
    const source = snapshotSource(srcOrN);
    if (!source) return false;
    const key = source.srcKey;
    _wsSet((state) => ({
      ...state,
      sourceCache: { ...(state.sourceCache || {}), [key]: source },
      sheets: state.sheets.map((sheet) => sheet.id === state.activeId
        ? { ...sheet, workingTexts: (sheet.workingTexts || []).map(String).includes(key) ? sheet.workingTexts : [...(sheet.workingTexts || []), key] }
        : sheet),
    }));
    return true;
  },
  removeWorkingText(srcOrN) {
    const key = sourceKey(srcOrN);
    _mutActive((sheet) => ({ ...sheet, workingTexts: (sheet.workingTexts || []).filter((item) => String(item) !== key) }));
  },
  uncollect(srcOrN) {
    const key = sourceKey(srcOrN);
    _wsSet((s) => ({ ...s, collected: s.collected.filter((x) => String(x) !== key) }));
  },
  clearCollected() { _wsSet((s) => ({ ...s, collected: [] })); },

  // saved-source folders (separate from the Research/source-sheet workflow)
  createFolder(name) {
    const id = wsUid("f");
    const clean = String(name || "Saved sources").trim() || "Saved sources";
    _wsSet((s) => ({ ...s, folders: [...(s.folders || []), { id, name: clean, items: [] }] }));
    return id;
  },
  saveToFolder(srcOrN, folderId) {
    const source = snapshotSource(srcOrN);
    if (!source) return false;
    let targetId = folderId;
    if (!targetId || !(_wsState.folders || []).some((folder) => folder.id === targetId)) targetId = WS.createFolder("Saved sources");
    const key = source.srcKey;
    _wsSet((s) => ({
      ...s,
      sourceCache: { ...(s.sourceCache || {}), [key]: source },
      folders: (s.folders || []).map((folder) => folder.id === targetId
        ? { ...folder, items: folder.items.map(String).includes(key) ? folder.items : [...folder.items, key] }
        : folder),
    }));
    return true;
  },
  removeFromFolder(srcOrN, folderId) {
    const key = sourceKey(srcOrN);
    _wsSet((s) => ({ ...s, folders: (s.folders || []).map((folder) => folder.id === folderId
      ? { ...folder, items: folder.items.filter((item) => String(item) !== key) }
      : folder) }));
  },
  clearFolder(folderId) {
    _wsSet((s) => ({ ...s, folders: (s.folders || []).map((folder) => folder.id === folderId ? { ...folder, items: [] } : folder) }));
  },
  deleteFolder(folderId) { _wsSet((s) => ({ ...s, folders: (s.folders || []).filter((folder) => folder.id !== folderId) })); },
  clearSaved() {
    _wsSet((s) => ({ ...s, collected: [], folders: (s.folders || []).map((folder) => ({ ...folder, items: [] })) }));
  },

  // sheet management
  newSheet(title) { const sh = _emptySheet(title || "Untitled source sheet"); _wsSet((s) => ({ ...s, sheets: [...s.sheets, sh], activeId: sh.id })); },
  switchSheet(id) { _wsSet((s) => ({ ...s, activeId: id })); },
  renameSheet(id, title) { _wsSet((s) => ({ ...s, sheets: s.sheets.map((sh) => sh.id === id ? { ...sh, title } : sh) })); },
  duplicateSheet(id) {
    _wsSet((s) => {
      const src = s.sheets.find((sh) => sh.id === id); if (!src) return s;
      const copy = { id: wsUid("s"), title: src.title + " (copy)", workingTexts: [...(src.workingTexts || [])], blocks: src.blocks.map((b) => ({ ...b, id: wsUid() })) };
      const i = s.sheets.findIndex((sh) => sh.id === id);
      const sheets = s.sheets.slice(); sheets.splice(i + 1, 0, copy);
      return { ...s, sheets, activeId: copy.id };
    });
  },
  deleteSheet(id) {
    _wsSet((s) => {
      let sheets = s.sheets.filter((sh) => sh.id !== id);
      if (!sheets.length) sheets = [_emptySheet("Untitled source sheet")];
      const activeId = s.activeId === id ? sheets[0].id : s.activeId;
      return { ...s, sheets, activeId };
    });
  },

  // active-sheet content
  setTitle(t) { _mutActive((sh) => ({ ...sh, title: t })); },
  addSourceBlock(srcOrN) {
    const source = snapshotSource(srcOrN);
    const srcN = source ? source.n : srcOrN;
    const srcKey = source ? source.srcKey : sourceKey(srcOrN);
    _wsSet((s) => ({
      ...s,
      sourceCache: source ? { ...(s.sourceCache || {}), [srcKey]: source } : (s.sourceCache || {}),
      sheets: s.sheets.map((sh) => sh.id === s.activeId ? { ...sh, blocks: [...sh.blocks, { id: wsUid(), type: "source", srcN, srcKey, source, note: "" }] } : sh),
    }));
  },
  addHeading(text) { _mutActive((sh) => ({ ...sh, blocks: [...sh.blocks, { id: wsUid(), type: "heading", text: text || "" }] })); },
  addComment(text, kind) { _mutActive((sh) => ({ ...sh, blocks: [...sh.blocks, { id: wsUid(), type: "comment", text: text || "", kind: kind || "note" }] })); },
  addText(data) { _mutActive((sh) => ({ ...sh, blocks: [...sh.blocks, { id: wsUid(), type: "text", title: "", citation: "", he: "", en: "", note: "", ...(data || {}) }] })); },

  // insert a freshly-built block at a position (index = where it lands)
  _make(kind, payload) {
    if (kind === "heading") return { id: wsUid(), type: "heading", text: "" };
    if (kind === "comment") return { id: wsUid(), type: "comment", text: "", kind: (payload && payload.kind) || "note" };
    if (kind === "text") return { id: wsUid(), type: "text", title: "", citation: "", he: "", en: "", note: "" };
    const source = snapshotSource(payload);
    return { id: wsUid(), type: "source", srcN: source ? source.n : payload, srcKey: source ? source.srcKey : sourceKey(payload), source, note: "" };
  },
  insertAt(index, kind, payload) {
    _mutActive((sh) => {
      const blocks = sh.blocks.slice();
      const at = Math.max(0, Math.min(blocks.length, index));
      blocks.splice(at, 0, WS._make(kind, payload));
      return { ...sh, blocks };
    });
  },
  updateBlock(id, patch) { _mutActive((sh) => ({ ...sh, blocks: sh.blocks.map((b) => b.id === id ? { ...b, ...patch } : b) })); },
  removeBlock(id) { _mutActive((sh) => ({ ...sh, blocks: sh.blocks.filter((b) => b.id !== id) })); },
  move(id, dir) {
    _mutActive((sh) => {
      const i = sh.blocks.findIndex((b) => b.id === id), j = i + dir;
      if (i < 0 || j < 0 || j >= sh.blocks.length) return sh;
      const blocks = sh.blocks.slice(); const [it] = blocks.splice(i, 1); blocks.splice(j, 0, it);
      return { ...sh, blocks };
    });
  },
  reorder(from, to) {
    _mutActive((sh) => {
      if (from === to || from < 0 || to < 0 || from >= sh.blocks.length || to >= sh.blocks.length) return sh;
      const blocks = sh.blocks.slice(); const [it] = blocks.splice(from, 1); blocks.splice(to, 0, it);
      return { ...sh, blocks };
    });
  },
  clear() { _mutActive((sh) => ({ ...sh, blocks: [] })); },

  // Create a new sheet from a guided blueprint without touching the current one.
  // Sources arrive as snapshots hydrated from the live corpus by Research.
  createGuidedSheet(blueprint, sources) {
    if (!blueprint || !Array.isArray(blueprint.blocks) || !Array.isArray(sources)) return false;
    const snapshots = sources.map((source) => snapshotSource(source)).filter(Boolean);
    const H = (block) => ({ id: wsUid(), type: "heading", text: block.text || "" });
    const C = (block) => ({ id: wsUid(), type: "comment", text: block.text || "", kind: block.kind || "note" });
    const B = (block) => {
      const source = snapshots[block.source];
      return source ? { id: wsUid(), type: "source", srcN: source.n, srcKey: source.srcKey, source, note: block.note || "" } : null;
    };
    const blocks = blueprint.blocks.map((block) => block.type === "heading" ? H(block) : block.type === "comment" ? C(block) : block.type === "source" ? B(block) : null).filter(Boolean);
    if (!blocks.length) return false;
    const sheet = { id: wsUid("s"), title: blueprint.title || "Sample shiur", workingTexts: snapshots[0] ? [snapshots[0].srcKey] : [], blocks };
    const sourceCache = Object.fromEntries(snapshots.map((source) => [source.srcKey, source]));
    _wsSet((state) => ({
      ...state,
      sourceCache: { ...(state.sourceCache || {}), ...sourceCache },
      collected: Array.from(new Set([...(state.collected || []).map(String), ...snapshots.map((source) => source.srcKey)])),
      sheets: [...state.sheets, sheet],
      activeId: sheet.id,
    }));
    return true;
  },

  // start a sheet from a structural template
  applyTemplate(kind) {
    const H = (text) => ({ id: wsUid(), type: "heading", text });
    const C = (text, kind) => ({ id: wsUid(), type: "comment", text, kind: kind || "note" });
    const tpls = {
      shiur: [H("Opening question"), C("Pose the kushya that frames the whole shiur.", "question"), H("The sources"), H("Building the answer"), C("Walk through the idea step by step.", "teaching"), H("The take-away")],
      chavrusa: [H("Girsa — read the text"), C("Read inside, word by word, before any analysis.", "instruction"), H("Questions to raise"), C("What is difficult? What seems redundant?", "question"), H("Compare & resolve")],
      essay: [H("Introduction"), H("Background sources"), H("Analysis"), H("Conclusion")],
    };
    const blocks = (tpls[kind] || tpls.essay);
    _mutActive((sh) => ({ ...sh, blocks: [...sh.blocks, ...blocks] }));
  },

  seedCurrentSources() {
    const srcs = (window.SOURCES || []).slice(0, 10);
    const title = (window.SICHA && (window.SICHA.titleEn || window.SICHA._liveRef || window.SICHA.id)) || "Current text";
    const blocks = [];
    if (srcs[0]) blocks.push({ id: wsUid(), type: "heading", text: "Core sources for this text" });
    srcs.slice(0, 5).forEach((src) => {
      const source = snapshotSource(src);
      blocks.push({ id: wsUid(), type: "source", srcN: source.n, srcKey: source.srcKey, source, note: "" });
    });
    if (srcs.length > 5) blocks.push({ id: wsUid(), type: "heading", text: "Additional connections" });
    srcs.slice(5, 10).forEach((src) => {
      const source = snapshotSource(src);
      blocks.push({ id: wsUid(), type: "source", srcN: source.n, srcKey: source.srcKey, source, note: "" });
    });
    _wsSet((s) => ({
      ...s,
      sourceCache: srcs.reduce((cache, src) => ({ ...cache, [sourceKey(src)]: snapshotSource(src) }), s.sourceCache || {}),
      collected: Array.from(new Set([...s.collected.map(String), ...srcs.map(sourceKey)])),
      sheets: s.sheets.map((sh) => sh.id === s.activeId ? {
        ...sh,
        title: sh.title && sh.blocks.length === 0 ? `${title} — source sheet` : sh.title,
        blocks: blocks.length ? blocks : [{ id: wsUid(), type: "comment", text: "No sources are loaded for the current text yet." }],
      } : sh),
    }));
  },

  // ── export to a Word / Google-Docs-compatible .doc file ──────────────────
  exportDoc(mode) {
    if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("export_started", { format: "doc", mode: mode || "sheet" });
    const sh = activeSheet(_wsState);
    const srcForBlock = (b) => sourceFromBlock(b);
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const highlightHtml = (text, highlights, lang) => {
      const value = String(text || "");
      const ranges = (highlights || []).filter((item) => item.lang === lang && item.start >= 0 && item.end > item.start).sort((a, b) => a.start - b.start);
      let cursor = 0;
      return ranges.map((item) => {
        const start = Math.max(cursor, Math.min(value.length, item.start));
        const end = Math.max(start, Math.min(value.length, item.end));
        const before = esc(value.slice(cursor, start));
        const marked = `<span class="hl hl-${esc(item.color || "gold")}">${esc(value.slice(start, end))}</span>`;
        cursor = end;
        return before + marked;
      }).join("") + esc(value.slice(cursor));
    };
    let secN = 0;
    const parts = sh.blocks.map((b) => {
      if (b.type === "heading") { secN++; return `<h2 class="h">${secN}. ${esc(b.text)}</h2>`; }
      if (b.type === "comment") return `<p class="note">${esc(b.text)}</p>`;
      if (b.type === "text") {
        const cite = `<p class="cite"><b>${esc(b.title || "Untitled source")}</b> ${esc(b.citation)}</p>`;
        const he = mode === "outline" || !b.he ? "" : `<p class="he" dir="rtl">${esc(b.he)}</p>`;
        const en = mode === "outline" || !b.en ? "" : `<p class="en">${esc(b.en)}</p>`;
        const note = b.note ? `<p class="snote">${esc(b.note)}</p>` : "";
        return `<div class="src">${cite}${he}${en}${note}</div>`;
      }
      const s = srcForBlock(b); if (!s) return "";
      const cite = `<p class="cite"><b>${esc(s.title)}</b> ${esc(s.citation)} &mdash; <span class="ty">${esc(s.type)}, ${esc(s.relationship)}</span></p>`;
      const he = mode === "outline" ? "" : `<p class="he" dir="rtl">${highlightHtml(s.he, b.highlights, "he")}</p>`;
      const en = mode === "outline" ? "" : `<p class="en">${highlightHtml(s.en, b.highlights, "en")}</p>`;
      const note = b.note ? `<p class="snote">${esc(b.note)}</p>` : "";
      return `<div class="src">${cite}${he}${en}${note}</div>`;
    }).join("\n");

    const css = `
      @page { size: A4; margin: 2cm; }
      body { font-family: Georgia, 'Times New Roman', serif; color:#1a1a1a; line-height:1.5; }
      .mast { font-family:Arial,sans-serif; font-weight:bold; font-size:11pt; letter-spacing:1px; border-bottom:2px solid #1a1a1a; padding-bottom:6px; margin-bottom:14px; }
      h1.title { font-size:20pt; margin:0 0 18px; }
      h2.h { font-size:14pt; color:#111; margin:20px 0 8px; }
      .src { margin:0 0 12px; }
      .cite { font-family:Arial,sans-serif; font-size:10.5pt; margin:0 0 3px; }
      .ty { color:#777; font-weight:normal; }
      .he { font-family:'Frank Ruhl Libre','Times New Roman',serif; font-size:15pt; line-height:1.9; margin:3px 0; direction:rtl; text-align:right; }
      .en { font-size:11pt; color:#333; margin:0 0 4px; }
      .note { font-style:italic; color:#444; border-left:3px solid #b08436; padding-left:10px; margin:10px 0; }
      .snote { font-size:10.5pt; color:#555; font-style:italic; background:#f4f1e9; padding:6px 10px; margin:4px 0 0; }
      .hl { border-radius:2px; padding:0 1px; } .hl-gold { background:#f5dea1; } .hl-blue { background:#cfe1f7; } .hl-green { background:#cfe9d6; } .hl-rose { background:#f4d1d7; }
      .colophon { font-family:Arial,sans-serif; font-size:9pt; color:#999; border-top:1px solid #ccc; margin-top:24px; padding-top:8px; }`;
    const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${esc(sh.title)}</title><style>${css}</style></head><body>` +
      `<div class="mast">מַעְיָנוֹת · MAYANOS — ${mode === "outline" ? "Shiur outline" : "Source sheet"}</div>` +
      `<h1 class="title">${esc(sh.title)}</h1>` + parts +
      `<p class="colophon">Prepared in Mayanos · ${today}</p></body></html>`;

    const blob = new Blob(["\ufeff", html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = (sh.title || "source-sheet").replace(/[^\w\u0590-\u05FF \-]+/g, "").trim().replace(/\s+/g, "-") + (mode === "outline" ? "-outline" : "") + ".doc";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  },
};

function useWorkspace() {
  const [st, setSt] = React.useState(_wsState);
  React.useEffect(() => { const fn = (s) => setSt(s); _wsSubs.add(fn); setSt(_wsState); return () => { _wsSubs.delete(fn); }; }, []);
  return st;
}

Object.assign(window, { WS, useWorkspace, activeSheet });
